import React, { useState, useEffect } from 'react';
import AdminSubPage from '../components/AdminSubPage';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Trash2, Zap } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const ExternalEventsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ event_name: '', entity_type: 'client', action_type: 'notify', target_url: '', is_active: true, trigger_condition: 'on_create', description: '' });

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try { const res = await api.get('/external-events'); setItems(res.data); }
    catch { setItems([]); }
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (!form.event_name) { toast.error('Event name is required'); return; }
    try { await api.post('/external-events', form); toast.success('Event created'); setShowModal(false); setForm({ event_name: '', entity_type: 'client', action_type: 'notify', target_url: '', is_active: true, trigger_condition: 'on_create', description: '' }); load(); }
    catch { toast.error('Failed'); }
  };

  const toggleActive = async (id, current) => {
    try { await api.put(`/external-events/${id}`, { is_active: !current }); load(); }
    catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    try { await api.delete(`/external-events/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Failed'); }
  };

  const actionColor = { notify: 'bg-blue-50 text-blue-700', webhook: 'bg-purple-50 text-purple-700', email: 'bg-emerald-50 text-emerald-700', sms: 'bg-amber-50 text-amber-700' };

  return (
    <AdminSubPage title="Manage External Events" breadcrumb={['Admin', 'System', 'External Events']} backPath="/dashboard/admin/system" actionLabel="+ Create Event" onAction={() => setShowModal(true)}>
      <div className="bg-white border border-gray-200" data-testid="external-events-table">
        <table className="w-full">
          <thead><tr className="bg-gray-50 border-b">
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Event</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Entity</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Trigger</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Action</th>
            <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Active</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="text-center py-8 text-gray-400">Loading...</td></tr> :
            items.length === 0 ? <tr><td colSpan={6} className="text-center py-8 text-gray-400"><Zap className="w-8 h-8 mx-auto mb-2 text-gray-300" />No external events configured.</td></tr> :
            items.map(item => (
              <tr key={item.id} className="border-b hover:bg-gray-50" data-testid={`event-row-${item.id}`}>
                <td className="px-4 py-3"><div className="font-medium text-sm text-[#1f1f1f]">{item.event_name}</div><div className="text-xs text-gray-400">{item.description || ''}</div></td>
                <td className="px-4 py-3 text-sm text-gray-600 capitalize">{item.entity_type}</td>
                <td className="px-4 py-3 text-sm text-gray-600 capitalize">{(item.trigger_condition || '').replace(/_/g, ' ')}</td>
                <td className="px-4 py-3"><span className={`px-2 py-0.5 text-xs font-medium capitalize ${actionColor[item.action_type] || 'bg-gray-50 text-gray-600'}`}>{item.action_type}</span></td>
                <td className="px-4 py-3 text-center"><Switch checked={item.is_active !== false} onCheckedChange={() => toggleActive(item.id, item.is_active)} /></td>
                <td className="px-4 py-3"><Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent aria-describedby="ee-desc"><DialogHeader><DialogTitle>Create External Event</DialogTitle></DialogHeader>
          <p id="ee-desc" className="sr-only">External event form</p>
          <div className="space-y-4">
            <div><Label>Event Name *</Label><Input value={form.event_name} onChange={e => setForm({...form, event_name: e.target.value})} data-testid="event-name-input" /></div>
            <div><Label>Description</Label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Entity Type</Label>
                <Select value={form.entity_type} onValueChange={v => setForm({...form, entity_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="client">Client</SelectItem><SelectItem value="loan">Loan</SelectItem>
                    <SelectItem value="savings">Savings</SelectItem><SelectItem value="office">Office</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Trigger Condition</Label>
                <Select value={form.trigger_condition} onValueChange={v => setForm({...form, trigger_condition: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="on_create">On Create</SelectItem><SelectItem value="on_update">On Update</SelectItem>
                    <SelectItem value="on_delete">On Delete</SelectItem><SelectItem value="on_approve">On Approve</SelectItem>
                    <SelectItem value="on_disburse">On Disburse</SelectItem><SelectItem value="on_repayment">On Repayment</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Action Type</Label>
                <Select value={form.action_type} onValueChange={v => setForm({...form, action_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="notify">Notify</SelectItem><SelectItem value="webhook">Webhook</SelectItem>
                    <SelectItem value="email">Email</SelectItem><SelectItem value="sms">SMS</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Target URL</Label><Input value={form.target_url} onChange={e => setForm({...form, target_url: e.target.value})} placeholder="https://..." /></div>
            </div>
            <div className="flex items-center gap-2"><Label>Active</Label><Switch checked={form.is_active} onCheckedChange={v => setForm({...form, is_active: v})} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button><Button onClick={handleSubmit} className="bg-[#1f1f1f] text-white" data-testid="event-submit-btn">Create Event</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminSubPage>
  );
};
export default ExternalEventsPage;
